import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import React from 'react';
import ProgressiveImage from '../../Image/ProgressiveImage';

const listItemStyle = {
  '&::before': {
    content: '"•"', // This is the bullet point
    display: 'inline-block', // Makes the bullet point inline with the text
    marginRight: '8px', // Adjust the space between the bullet point and text
    fontSize: 'inherit', // Ensures the bullet point scales with the text size
    color: 'currentColor', // Bullet point color matches the text color
  },
};

const subListItemStyle = {
  '&::before': {
    content: '"- "', // This is the sub-bullet point
    display: 'inline-block', // Makes the sub-bullet point inline with the text
    marginRight: '8px', // Adjust the space between the sub-bullet point and text
    fontSize: 'inherit', // Ensures the sub-bullet point scales with the text size
    color: 'currentColor', // Sub-bullet point color matches the text color
  },
  marginLeft: '16px', // Indent sub-benefits for clarity
};

const imageTitle = 'Visual Representation of the annual pass';
const imageAlt = 'Jahrespass';

/**
 * @typedef {object} DataSource
 * @property {string} benefitsHeading - Heading for the benefits section
 * @property {string} benefits - Comma-separated list of benefits, can include sub-benefits in parentheses
 * @property {string} heading - Main heading of the component
 * @property {string} [priceHeading] - Optional heading for the price section
 * @property {string} [priceSubHeading] - Optional sub-heading for the price section
 * @property {string} subHeading - Sub-heading text below main heading
 * @property {string} imageUrl - URL for the pass image
 * @property {string} buttonHeading - Text to display on the CTA button
 * @property {string} toPassButtonLink - URL for the button link
 */

/**
 * StoryblokPassPreset component displays information about an annual pass with benefits,
 * pricing, and a call-to-action button alongside an image.
 *
 * @param {object} props - Component props
 * @param {object} props.blok - Storyblok block data
 * @param {DataSource} props.blok.dataSource - Data source containing component content
 * @returns {React.ReactElement} StoryblokPassPreset component
 */
const StoryblokPassPreset = ({ blok }) => {
  const { dataSource } = blok || {};

  // StoryblokPage.duck.js only attaches a dataSource to pass_preset bloks that
  // sit on the content path it walks, and it substitutes an empty object when
  // the Storyblok datasource itself 404s. Both leave this blok without content,
  // which the sibling presets already treat as "render nothing" rather than
  // letting the destructuring take the whole page down.
  if (!dataSource || typeof dataSource !== 'object' || typeof dataSource.benefits !== 'string') {
    return null;
  }

  const {
    benefitsHeading,
    benefits,
    heading,
    priceHeading,
    priceSubHeading,
    subHeading,
    imageUrl,
    buttonHeading,
    toPassButtonLink,
  } = dataSource;

  const benefitsArray = benefits.split(',').map((item) => item.trim());

  const emptyChar = 'empty'; // this char is used in storyblok datasources as empty field since it cant be truly empty

  return (
    <Grid container>
      <Grid size={{ md: 6, xs: 11 }}>
        <Typography component="h2" gutterBottom variant="h2">
          {heading}
        </Typography>
        <Typography component="h3" mb={4} variant="body1">
          {subHeading}
        </Typography>
        <Typography component="h3" sx={{ fontWeight: 'bold' }} variant="body1">
          {benefitsHeading}
        </Typography>
        <List>
          {benefitsArray.map((text, index) => {
            const mainBenefit = text.replace(/\(.*\)/, '').trim(); // remove brackets() and content inside from the text
            const subBenefitMatch = text.match(/\((.*)\)/); // find inner content between brackets
            const subBenefit = subBenefitMatch ? subBenefitMatch[1].trim() : null;

            return (
              <React.Fragment key={index}>
                <ListItem sx={listItemStyle}>
                  <Typography component="h3" sx={{ fontWeight: 'bold' }} variant="body1">
                    {mainBenefit}
                  </Typography>
                </ListItem>
                {subBenefit ? (
                  <ListItem sx={subListItemStyle}>
                    <Typography component="h4" variant="body2">
                      {subBenefit}
                    </Typography>
                  </ListItem>
                ) : null}
              </React.Fragment>
            );
          })}
        </List>

        {priceHeading?.trim() !== emptyChar ? (
          <Typography component="h3" sx={{ fontWeight: 'bold', mb: 0 }} variant="body1">
            {priceHeading}
          </Typography>
        ) : null}
        {priceSubHeading?.trim() !== emptyChar ? (
          <Typography
            component="h4"
            sx={{ fontWeight: 'lighter', mb: 2, fontSize: '14px' }}
            variant="body2"
          >
            {priceSubHeading}
          </Typography>
        ) : null}

        <Button
          color="primary"
          href={toPassButtonLink}
          sx={{ marginTop: '16px' }}
          variant="contained"
        >
          {buttonHeading}
        </Button>
      </Grid>
      <Grid size={{ md: 6, xs: 12 }}>
        <ProgressiveImage alt={imageAlt} disableBlur src={imageUrl} title={imageTitle} />
      </Grid>
    </Grid>
  );
};

export default StoryblokPassPreset;
